import { Item } from "@astryxdesign/core/Item";
import { ProgressBar } from "@astryxdesign/core/ProgressBar";
import { HStack, StackItem, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { Token } from "@astryxdesign/core/Token";
import type { DueEntry, SubPayment, Subscription } from "../../../shared/money";
import {
  dueColor,
  dueLabel,
  formatMoney,
  formatUah,
  installmentProgress,
  nextDueFor,
} from "../../lib/money";

interface Props {
  sub: Subscription;
  due: DueEntry[];
  payments: SubPayment[];
  onOpen: (id: string) => void;
}

export function SubRow({ sub, due, payments, onOpen }: Props) {
  const next = nextDueFor(sub.id, due);
  const foreign = sub.currency !== "UAH";
  const progress = sub.kind === "installment" ? installmentProgress(sub, payments) : null;

  return (
    <Item
      as="li"
      density="balanced"
      align={progress ? "start" : "center"}
      onClick={() => onOpen(sub.id)}
      label={
        <Text type="body" weight="medium" maxLines={1} color={sub.paused ? "secondary" : "primary"}>
          {sub.name}
        </Text>
      }
      description={
        <VStack gap={1}>
          <HStack gap={1.5} align="center" wrap="wrap">
            {sub.paused ? <Token size="sm" color="gray" label="paused" /> : null}
            {sub.kind === "recurring" ? (
              <Text type="supporting" size="xsm">
                {sub.cycle === "yearly" ? "yearly" : "monthly"}
              </Text>
            ) : (
              <Text type="supporting" size="xsm">
                {progress?.paid}/{progress?.total} paid · {formatUah(progress?.leftUah ?? 0)} left
              </Text>
            )}
            {next ? (
              <Token size="sm" color={dueColor(next.daysLeft)} label={dueLabel(next.daysLeft)} />
            ) : null}
            {sub.card ? (
              <Text type="supporting" size="xsm">
                {sub.card}
              </Text>
            ) : null}
          </HStack>
          {progress ? (
            <ProgressBar value={Math.round(progress.ratio * 100)} label="Installment progress" isLabelHidden />
          ) : null}
        </VStack>
      }
      endContent={
        <StackItem>
          <VStack gap={0} hAlign="end">
            <Text type="body" weight="medium">
              {formatMoney(sub.amount, sub.currency)}
            </Text>
            {foreign ? (
              <Text type="supporting" size="xsm">
                ≈ {formatUah(next?.amountUah ?? 0)}
              </Text>
            ) : null}
          </VStack>
        </StackItem>
      }
    />
  );
}
